import { useCallback, useState } from 'react'
import type { CustomSegment, ProductSegmentRule } from '@/components/v2/products/segments/segmentPresets'

const STORAGE_KEY = 'goki:product-segments'
type Stored = { selectedId: string; customSegments: CustomSegment[] }
const DEFAULTS: Stored = { selectedId: 'all', customSegments: [] }

function persist(next: Stored): Stored {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* ignore (private mode / quota) */
  }
  return next
}

function load(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const p = JSON.parse(raw) as Partial<Stored>
      const customSegments = Array.isArray(p.customSegments) ? p.customSegments : DEFAULTS.customSegments
      return { ...DEFAULTS, ...p, customSegments }
    }
  } catch {
    /* ignore (private mode / bad JSON) */
  }
  return DEFAULTS
}

export function useProductSegments() {
  const [s, setS] = useState<Stored>(load)
  const patch = useCallback((p: Partial<Stored>) => setS((prev) => persist({ ...prev, ...p })), [])
  const mut = useCallback((fn: (l: CustomSegment[]) => CustomSegment[]) =>
    setS((prev) => persist({ ...prev, customSegments: fn(prev.customSegments) })), [])
  return {
    selectedId: s.selectedId,
    customSegments: s.customSegments,
    select: (id: string) => patch({ selectedId: id }),
    addSegment: (seg: Omit<CustomSegment, 'id' | 'pinned'>) =>
      mut((l) => [...l, { ...seg, id: crypto.randomUUID(), pinned: false }]),
    renameSegment: (id: string, name: string) => mut((l) => l.map((x) => (x.id === id ? { ...x, name } : x))),
    recolorSegment: (id: string, color: string) => mut((l) => l.map((x) => (x.id === id ? { ...x, color } : x))),
    setPinned: (id: string, pinned: boolean) => mut((l) => l.map((x) => (x.id === id ? { ...x, pinned } : x))),
    updateRule: (id: string, rule: ProductSegmentRule) => mut((l) => l.map((x) => (x.id === id ? { ...x, rule } : x))),
    deleteSegment: (id: string) => mut((l) => l.filter((x) => x.id !== id)),
  }
}
